import dayjs from 'dayjs';
import sumBy from 'lodash/sumBy';

import {
  CHANGE_OF_MIND_WITHIN_REFUND_GUARANTEE_PERIOD,
  LATE_CHANGE_OF_MIND_OR_BAD_EXPERIENCE_OR_UNFORTUNATE_EVENTS,
} from '~/consts/refund';
import { ROLE_ADMIN_USER, ROLE_CX_TEAM_LEADER } from '~/consts/roles';

import { RefundPreviewMethod } from '~/services/PaymentsService';

import { RefundMethods } from './RefundUtils';

export const REFUND_ALERTS_SLACK_CHANNEL = 'refund-alerts';
export const CASH_REFUND_ALERT_THRESHOLD = 5000;
export const OUTSIDE_POLICY_TL_CASH_THRESHOLD = 10000;

const CASH_REFUND_METHOD_TYPES = new Set([
  'stripe',
  'braintree',
  'paypal',
  'razorpay',
  'zip',
  'latitude',
  'latitude_pay',
  'klarna',
  'bridgerpay',
  'partner',
  'azupay_pay_id',
  'manual',
  'manual-latitude',
]);

export interface RefundGovernancePolicyContext {
  isWithinChangeOfMind?: boolean;
}

export interface RefundGovernanceUserContext {
  roles?: Array<string>;
  createdDate?: string | null;
}

export interface RefundGovernanceInput {
  refundMethod?: RefundMethods;
  refundReason?: string;
  previewMethods?: Array<RefundPreviewMethod>;
  isPreviewLoading?: boolean;
  hasPreviewError?: boolean;
  policyContext?: RefundGovernancePolicyContext;
  userContext?: RefundGovernanceUserContext;
  now?: Date;
}

export interface RefundGovernanceResult {
  cashRefundAmount: number;
  hasCashRefund: boolean;
  isDefaultMethod: boolean;
  isSensitiveContext: boolean;
  isTeamLeader: boolean;
  isNewAgent: boolean;
  isBlocked: boolean;
  warningMessages: Array<string>;
  blockingMessages: Array<string>;
  shouldAlertSlack: boolean;
}

export function getCashRefundAmount(methods: Array<RefundPreviewMethod> = []): number {
  return sumBy(
    methods.filter((method) => CASH_REFUND_METHOD_TYPES.has(method.type)),
    'amount',
  );
}

export function isTeamLeader(userContext?: RefundGovernanceUserContext): boolean {
  return Boolean(userContext?.roles?.includes(ROLE_ADMIN_USER));
}

export function canReleaseNonRefundableDeposit(roles?: Array<string>): boolean {
  return Boolean(roles?.some((role) => role === ROLE_ADMIN_USER || role === ROLE_CX_TEAM_LEADER));
}

export function isNewAgent(createdDate?: string | null, now: Date = new Date()): boolean {
  if (!createdDate) {
    return false;
  }

  const parsedCreatedDate = dayjs(createdDate);
  if (!parsedCreatedDate.isValid()) {
    return false;
  }

  return parsedCreatedDate.isAfter(dayjs(now).subtract(6, 'month'));
}

export function getRefundGovernance(input: RefundGovernanceInput): RefundGovernanceResult {
  const cashRefundAmount = getCashRefundAmount(input.previewMethods);
  const hasCashRefund = cashRefundAmount > 0;
  const isDefaultMethod = input.refundMethod === 'Back To Original';
  const isChangeOfMindReason = input.refundReason === CHANGE_OF_MIND_WITHIN_REFUND_GUARANTEE_PERIOD;
  const isOutsidePolicyReason = input.refundReason === LATE_CHANGE_OF_MIND_OR_BAD_EXPERIENCE_OR_UNFORTUNATE_EVENTS;
  const isSensitiveContext =
    (isChangeOfMindReason && input.policyContext?.isWithinChangeOfMind === false) || isOutsidePolicyReason;
  const teamLeader = isTeamLeader(input.userContext);
  const newAgent = isNewAgent(input.userContext?.createdDate, input.now);
  const cashStatusUnknown =
    !!input.refundMethod && isSensitiveContext && (input.isPreviewLoading || input.hasPreviewError);
  const warningMessages: Array<string> = [];
  const blockingMessages: Array<string> = [];

  if (cashStatusUnknown) {
    blockingMessages.push(
      input.hasPreviewError
        ? 'Refund method checks could not be loaded. Please retry before submitting this refund.'
        : 'Refund method checks are still loading. Please wait before submitting this refund.',
    );
  }

  if (isDefaultMethod && hasCashRefund && isChangeOfMindReason && input.policyContext?.isWithinChangeOfMind === false) {
    if (teamLeader) {
      warningMessages.push(
        'Warning: this Default refund includes cash outside of the Change of Mind period. Please make sure this is the correct refund method before proceeding.',
      );
    } else {
      blockingMessages.push(
        'Default is restricted outside the Change of Mind period. Escalate to a TL if a cash refund is required.',
      );
    }
  }

  if (isDefaultMethod && hasCashRefund && isOutsidePolicyReason) {
    warningMessages.push(
      'Warning: this is a cash refund outside of cancellation policy. Please make sure this is the correct refund method and escalate to a TL if you are not sure.',
    );

    if (!teamLeader && cashRefundAmount > OUTSIDE_POLICY_TL_CASH_THRESHOLD) {
      blockingMessages.push('Only TLs can process Default cash refunds over $10,000 outside cancellation policy.');
    }

    if (newAgent && !teamLeader) {
      blockingMessages.push(
        'New agents cannot process Default cash refunds outside cancellation policy. Escalate this refund to a TL.',
      );
    }
  }

  return {
    cashRefundAmount,
    hasCashRefund,
    isDefaultMethod,
    isSensitiveContext,
    isTeamLeader: teamLeader,
    isNewAgent: newAgent,
    isBlocked: blockingMessages.length > 0,
    warningMessages,
    blockingMessages,
    shouldAlertSlack:
      cashRefundAmount > CASH_REFUND_ALERT_THRESHOLD &&
      ((isChangeOfMindReason && input.policyContext?.isWithinChangeOfMind === false) || isOutsidePolicyReason),
  };
}

function formatOrderLink(orderId: string, adminBaseUrl: string): string {
  return `Order: <${adminBaseUrl}/purchases/${orderId}|${orderId}>`;
}

function formatAdminLink({
  adminBaseUrl,
  adminId,
  adminEmail,
}: {
  adminBaseUrl: string;
  adminId: string;
  adminEmail: string;
}): string {
  return `Admin: <${adminBaseUrl}/users/${adminId}|${adminEmail}>`;
}

export function buildRefundAlertMessage({
  orderId,
  itemId,
  bookingNumber,
  customerName,
  refundReason,
  refundMethod,
  cashRefundAmount,
  currencyCode,
  environment,
  adminEmail,
  adminId,
  adminBaseUrl,
}: {
  orderId: string;
  itemId: string;
  bookingNumber?: string;
  customerName?: string;
  refundReason?: string;
  refundMethod?: string;
  cashRefundAmount: number;
  currencyCode: string;
  environment?: string;
  adminEmail: string;
  adminId: string;
  adminBaseUrl: string;
}): string {
  return [
    '*High-value cash refund submitted*',
    `Environment: ${environment || 'Unknown'}`,
    formatOrderLink(orderId, adminBaseUrl),
    `Item: ${itemId}`,
    bookingNumber ? `Booking number: ${bookingNumber}` : undefined,
    customerName ? `Customer: ${customerName}` : undefined,
    `Reason: ${refundReason || 'Unknown'}`,
    `Refund method: ${refundMethod || 'Unknown'}`,
    `Cash refund amount: ${currencyCode} ${cashRefundAmount.toFixed(2)}`,
    formatAdminLink({
      adminBaseUrl,
      adminId,
      adminEmail,
    }),
  ]
    .filter(Boolean)
    .join('\n');
}
